import { appInsights } from "./AppInsights";
import { configService } from "./Config/ConfigService";

export class IdentityServerKnownClient {
    static physicianPortal: string = "physicianportal";
}

export interface IErrorHandlerOpts {
    handleErrorRedirect ?: boolean;
}

class AbpnClient {
    private _baseUri: string;
    public _errorPage: string;


    private async ensureConfiguration() {
        if (this._baseUri) return;

        this._baseUri = (await configService.getConfiguration()).AbpnApiUrl;
    }

    private async getDefaultHeaders(authContext: any) {
        const token = authContext?.isAuthenticated() ? await authContext.getAccessToken() : null;
        const headers = token ? [['Authorization', `Bearer ${token}`]] : [];
        headers.push(['Pragma', 'no-cache']);

        var onBehalfOf = await authContext?.getOnBehalfOf();

        if (onBehalfOf) {
            headers.push(['X-OBO-PhysicianId', onBehalfOf]);
        }
        return headers;
    }

    private async validateResponse(response: Response): Promise<Response> {
        if (response.status === 401) {
            return Promise.reject(response.status);
        }
        if (!response.ok) {
            var clone = response.clone();
            const text = await clone.text();
            throw Error(`(${response.statusText}) ${text}}`);
        }
        return Promise.resolve(response);
    }

    private async castResponse<T>(response: Response): Promise<T | null> {
        const text = await response.text();
        if (!text) {
            return null;
        }
        return JSON.parse(text) as T;
    }

    private async handleError(error: any, authContext: any, errorHandlerOpts ?: IErrorHandlerOpts): Promise<any> {       
        if (error.message === "Unauthorized" || error === 401) {
            authContext.logout();
            await appInsights.logError(error);
            return Promise.reject(error);
        }

        await appInsights.logError(error);
        const handleErrorRedirect = !(errorHandlerOpts && errorHandlerOpts.handleErrorRedirect === false);
        if (handleErrorRedirect) {
            window.location.href = this._errorPage;
        }
        else {
            throw error; //Expect error to be handled in caller component
        }

        return Promise.reject(error);

    }

    setErrorPage(errorPage: string) {

        this._errorPage = errorPage;
    }

    async post<T>(relativePath: string, body: any, authContext: any, errorHandlerOpts ?: IErrorHandlerOpts): Promise<T> {
        await this.ensureConfiguration();

        const headers : any = await this.getDefaultHeaders(authContext);
        headers.push(["Content-Type", "application/json"]);
        return fetch(this._baseUri + relativePath,
            {
                method: "post",
                headers: headers,
                body: JSON.stringify(body)
            })
            .then(this.validateResponse)
            .then(response => this.castResponse<T>(response))
            .catch(error => this.handleError(error, authContext, errorHandlerOpts));
    }

    async postStringResponse<T>(relativePath: string, body: any, authContext: any): Promise<T> {
        await this.ensureConfiguration();

        const headers : any = await this.getDefaultHeaders(authContext);
        headers.push(["Content-Type", "application/json"]);
        return fetch(this._baseUri + relativePath,
            {
                method: "post",
                headers: headers,
                body: JSON.stringify(body)
            })
            .then(this.validateResponse)
            .catch(error => this.handleError(error, authContext));
    }

    async get<T>(relativePath: string, authContext: any): Promise<T> {
        await this.ensureConfiguration();

        const headers : any = await this.getDefaultHeaders(authContext);
        return fetch(this._baseUri + relativePath, {
            method: "GET",
            headers: headers
        })
            .then(this.validateResponse)
            .then(response => this.castResponse<T>(response))
            .catch(error => this.handleError(error, authContext));
    }

    async getResponse(relativePath: string, authContext: any): Promise<any> {
        await this.ensureConfiguration();

        const headers : any = await this.getDefaultHeaders(authContext);
        return fetch(this._baseUri + relativePath, {
            headers: headers
        })
            .then(this.validateResponse)
            .catch(error => this.handleError(error, authContext));
    }
}
export const abpnClient = new AbpnClient();